import React, { useContext, useEffect, useMemo, useState } from "react";
import LocaleContext from "../../../constants/localization";
import { MapContainer } from "react-leaflet";
import { isEmpty } from "lodash";
import { getGeometryBounds } from "actions/spectrumActions";
import MapLayersComponent from "components/common/MapLayersComponent";
import GeometryFieldComponent from "components/common/GeometryFieldComponent";
import API from "constants/api";
import MAP from "constants/mapConstants";
import { finCRS } from "../../../map/finCRS";

import * as L from "leaflet";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import "proj4";
import "proj4leaflet";
import "leaflet/dist/leaflet.css";
import Header from "../common/Header";
import MapClickEventComponent from "../../common/MapClickEventComponent";

export default function LocationEdit({ field }) {
  const [map, setMap] = useState(null);
  const [display, setDisplay] = useState(false);
  const [mapCenter, setMapCenter] = useState([
    61.777460617359054, 24.7476951251703,
  ]);
  const [zoom, setZoom] = useState(0);
  const [wfsEnabled, setWfsEnabled] = useState(false);
  const [getFeatureInfoEnabled, setGetFeatureInfoEnabled] = useState(false);
  const [layerConfig, setLayerConfig] = useState({});
  const LS = useContext(LocaleContext);

  useEffect(() => {
    fetch(API.MAP_CONFIG)
      .then((res) => res.json())
      .then((res) => {
        if (!isEmpty(res)) {
          if (!isEmpty(res.center)) {
            setMapCenter([res.center.x, res.center.y]);
            setZoom(res.center.zoom);
          }
          setWfsEnabled(res.wfs.enabled);
          setGetFeatureInfoEnabled(res.getFeatureInfo.enabled);
          if (!isEmpty(res.layers)) {
            setLayerConfig(res.layers);
          }
        }
      })
      .catch((e) => {
        if (!(e instanceof SyntaxError)) {
          throw e;
        }
      });
  }, []);

  const displayMap = useMemo(
    () => (
      <div>
        <MapContainer
          center={mapCenter}
          style={{ height: "75vh", width: "100%", cursor: "crosshair" }}
          zoom={zoom}
          crs={finCRS}
          scrollWheelZoom={true}
          maxBounds={[
            [57.15273773771963, 50.28861573758486],
            [73.61064411453262, 10.730096654154679],
          ]}
          whenCreated={(map) => {
            setMap(map);
            map.pm.setLang("customName", LS.map.tooltips);
            if (field.values.length > 0) {
              map.fitBounds(getGeometryBounds(field));
            }
            L.DomUtil.addClass(map._container, "crosshair-cursor-enabled");
          }}
        >
          <GeometryFieldComponent field={field} />
          <MapLayersComponent
            layerConfig={layerConfig}
            wfsEnabled={wfsEnabled}
          />
          <MapClickEventComponent
            searchView={false}
            getFeatureInfoEnabled={getFeatureInfoEnabled}
            overlayMapDetails={layerConfig.overlayMapDetails}
          />
        </MapContainer>
      </div>
    ),
    [mapCenter, zoom, wfsEnabled, getFeatureInfoEnabled, layerConfig]
  );

  if (display) {
    return (
      <div className="field">
        <Header field={field} />
        <button
          type="button"
          onClick={() => {
            setDisplay(false);
          }}
        >
          {LS.actions.close}
        </button>
        {map && <Controls map={map} field={field} />}
        {displayMap}
      </div>
    );
  } else {
    return (
      <div className="field">
        <Header field={field} />
        <button
          type="button"
          onClick={() => {
            setDisplay(true);
          }}
        >
          {LS.actions.open}
        </button>
      </div>
    );
  }
}

const Controls = ({ map, field }) => {
  const getPoint = (e) => {
    const point = e.layer.getLatLng();
    return `${point.lat} ${point.lng}`;
  };

  const getPolygon = (e) => {
    const points = e.layer.getLatLngs()[0];
    let value = [];
    points.forEach((point) => {
      value.push(`${point.lat} ${point.lng}`);
    });
    value.push(value[0]);
    return value.join(", ").toString();
  };

  const getPoints = (e) => {
    const points = e.layer.getLatLngs();
    let value = [];
    points.forEach((point) => {
      value.push(`${point.lat} ${point.lng}`);
    });
    return value.join(", ").toString();
  };

  const onCreate = (e) => {
    e.layer.setStyle({ color: MAP.DEFAULT_COLOR });
    if (e.shape === "CircleMarker") {
      const point = getPoint(e);
      e.layer.bindPopup(point);
      field.values.push(point);
    } else if (e.shape === "Rectangle" || e.shape === "Polygon") {
      const points = getPolygon(e);
      e.layer.bindPopup(points);
      field.values.push(points);
    } else if (e.shape === "Line") {
      const points = getPoints(e);
      e.layer.bindPopup(points);
      field.values.push(points);
    }
  };

  const onRemove = (e) => {
    if (e.shape === "CircleMarker") {
      const point = getPoint(e);
      const index = field.values.findIndex((v) => v === point);
      field.values.splice(index, 1);
    } else if (e.shape === "Rectangle" || e.shape === "Polygon") {
      const points = getPolygon(e);
      const index = field.values.findIndex((v) => v === points);
      field.values.splice(index, 1);
    } else if (e.shape === "Line") {
      const points = getPoints(e);
      const index = field.values.findIndex((v) => v === points);
      field.values.splice(index, 1);
    }
  };

  useEffect(() => {
    map.on("pm:create", onCreate);
    map.on("pm:remove", onRemove);
    map.pm.addControls({
      position: "topleft",
      drawCircle: false,
      drawMarker: false,
      editMode: false,
      dragMode: false,
      rotateMode: false,
      cutPolygon: false,
      drawText: false,
    });
    map.pm.setGlobalOptions({
      templineStyle: {
        radius: 5,
      },
      pathOptions: {
        radius: 5,
      },
    });

    return () => {
      map.off("pm:create", onCreate);
      map.off("pm:remove", onRemove);
    };
  }, [map, onCreate, onRemove]);

  return null;
};
